import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Send, RefreshCw, Smartphone, LogOut, ChevronLeft, Search,
         MessageCircle, Check, AlertTriangle, Trash2, Paperclip, Mic, Square,
         FileText, Loader2, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORO = '#c9a84c';
const VERDE = '#25D366';

// La sección WhatsApp.
//
// Es WhatsApp Web metido en el CRM: una sola sesión de la empresa que ve todo
// el que tenga acceso a esta sección. NO hay bot ni respuestas automáticas —
// escribe una persona. Por eso el mensaje sale con el nombre de quien lo manda
// guardado en la ficha, para saber después quién contestó qué.

function useEsMovil(limite = 820) {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= limite : false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${limite}px)`);
    const f = (e) => setM(e.matches);
    setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', f) : mq.addListener(f);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', f) : mq.removeListener(f));
  }, [limite]);
  return m;
}

const cuando = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString())
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

// Con quién se habla es SIEMPRE el móvil. `wa_id` puede ser el número interno
// de WhatsApp, que no sirve ni para llamar ni para dar de alta una ficha; por
// eso el móvil viene aparte y puede faltar.
const numeroDe = (x) => (x && (x.telefono || x.wa_id)) || '';

const telefono = (n) => {
  const s = String(n || '').replace(/\D/g, '');
  if (s.startsWith('34') && s.length === 11) return `+34 ${s.slice(2, 5)} ${s.slice(5, 8)} ${s.slice(8)}`;
  return '+' + s;
};

export function SeccionWhatsApp({ orgId, abrirNumero, onAbierto }) {
  const [estado, setEstado] = useState(null);
  const [qr, setQr] = useState(null);
  const [convs, setConvs] = useState([]);
  const [abierta, setAbierta] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [cargando, setCargando] = useState(true);
  const esMovil = useEsMovil();
  const finRef = useRef(null);

  const conectado = estado?.estado === 'conectado';

  // ── Dar de alta al contacto en el CRM ─────────────────────────────────────
  const [asignando, setAsignando] = useState(null);

  const asignar = async (categoria) => {
    if (!abierta) return;
    if (!abierta.wa_conversation_id) {
      toast.error('Escribe algo primero: la conversación todavía no existe en el CRM');
      return;
    }
    const quien = abierta.profile_name || telefono(numeroDe(abierta));
    const nombre = window.prompt(
      `Nombre para la ficha de ${categoria}:`, abierta.profile_name || '');
    if (nombre === null) return;     // le ha dado a cancelar
    setAsignando(categoria);
    try {
      const r = await axios.post(
        `${API}/wa/conversaciones/${abierta.wa_conversation_id}/asignar`,
        { categoria, nombre: nombre.trim() || null, org_id: orgId });
      toast.success(`${quien} dado de alta como ${categoria}`);
      cargarConvs();
      abrir({ ...abierta, profile_name: r.data?.nombre || abierta.profile_name });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear la ficha');
    }
    setAsignando(null);
  };


  // Cuando se pide abrir el chat de un numero concreto (desde una ficha del
  // CRM), se busca entre las conversaciones. Si no existe todavia porque nunca
  // se ha hablado con esa persona, se abre una en blanco: escribir el primer
  // mensaje la creara de verdad.
  useEffect(() => {
    if (!abirNumeroValido(abrirNumero)) return;
    const n = String(abrirNumero).replace(/\D/g, '');
    const existente = convs.find((c) => String(numeroDe(c)).replace(/\D/g, '').endsWith(n.slice(-9)));
    if (existente) {
      abrir(existente);
    } else {
      setAbierta({ wa_conversation_id: null, wa_id: n, telefono: n, profile_name: null });
      setMensajes([]);
    }
    onAbierto && onAbierto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abrirNumero, convs.length]);

  function abirNumeroValido(x) {
    return !!x && String(x).replace(/\D/g, '').length >= 9;
  }


  // ── Adjuntos y notas de voz ───────────────────────────────────────────────
  const ficheroRef = useRef(null);
  const grabadoraRef = useRef(null);
  const trozosRef = useRef([]);
  const [subiendo, setSubiendo] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);

  const mandarArchivo = async (fichero, comoNotaDeVoz = false) => {
    if (!abierta || !fichero) return;
    if (fichero.size > 16 * 1024 * 1024) {
      toast.error('El archivo pesa más de 16 MB');
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', fichero, fichero.name || (comoNotaDeVoz ? 'nota.ogg' : 'archivo'));
      fd.append('para', numeroDe(abierta));
      if (orgId) fd.append('organization_id', orgId);
      await axios.post(`${API}/wa/enviar-media`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      abrir(abierta);
      cargarConvs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el archivo');
    }
    setSubiendo(false);
  };

  const empezarGrabacion = async () => {
    try {
      const flujo = await navigator.mediaDevices.getUserMedia({ audio: true });
      // WhatsApp entiende bien ogg/opus, que es lo que graba el navegador.
      const tipo = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus' : 'audio/webm';
      const g = new MediaRecorder(flujo, { mimeType: tipo });
      trozosRef.current = [];
      g.ondataavailable = (e) => e.data.size && trozosRef.current.push(e.data);
      g.onstop = async () => {
        flujo.getTracks().forEach((t) => t.stop());
        const blob = new Blob(trozosRef.current, { type: tipo });
        if (blob.size > 800) {
          await mandarArchivo(new File([blob], 'nota.ogg', { type: tipo }), true);
        }
      };
      g.start();
      grabadoraRef.current = g;
      setGrabando(true);
      setSegundos(0);
    } catch (e) {
      toast.error('No he podido acceder al micrófono');
    }
  };

  const pararGrabacion = () => {
    try { grabadoraRef.current?.stop(); } catch (e) {}
    grabadoraRef.current = null;
    setGrabando(false);
  };

  // El contador de la grabación
  useEffect(() => {
    if (!grabando) return undefined;
    const t = setInterval(() => setSegundos((n) => {
      // Tope de dos minutos: una nota más larga nadie la escucha, y el modelo
      // de Whisper tarda en transcribirla.
      if (n >= 120) { pararGrabacion(); return n; }
      return n + 1;
    }), 1000);
    return () => clearInterval(t);
  }, [grabando]);


  // Borrar un chat del CRM. Importante decir lo que NO hace: no toca el
  // WhatsApp de verdad. Si el contacto vuelve a escribir, la conversacion
  // reaparece; lo que se borra es la copia y su historial aqui dentro.
  const borrarChat = async (c) => {
    const quien = c.profile_name || telefono(numeroDe(c));
    if (!window.confirm(
      `Eliminar del CRM la conversacion con ${quien}?\n\n` +
      `Se borra el historial guardado aqui. En WhatsApp no se borra nada, y si ` +
      `vuelve a escribir la conversacion aparecera otra vez.`)) return;
    try {
      await axios.delete(`${API}/wa/conversaciones/${c.wa_conversation_id}`);
      toast.success('Conversacion eliminada del CRM');
      if (abierta?.wa_conversation_id === c.wa_conversation_id) setAbierta(null);
      cargarConvs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };


  const mirarEstado = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wa/estado`);
      setEstado(data);
      if (data.estado !== 'conectado') {
        const q = await axios.get(`${API}/wa/qr`);
        setQr(q.data.qr || null);
      } else setQr(null);
      return data;
    } catch (e) {
      setEstado({ disponible: false, estado: 'sin_puente' });
      return null;
    } finally { setCargando(false); }
  }, []);

  const cargarConvs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wa/conversaciones?org_id=${orgId || ''}`);
      setConvs(data.conversaciones || []);
    } catch (e) { /* si no hay sesión aún, no hay nada que cargar */ }
  }, [orgId]);

  // Mientras no esté vinculado se mira cada 4 s (el QR caduca). Ya conectado,
  // cada 8 s basta para refrescar la bandeja sin machacar el servidor.
  useEffect(() => {
    let vivo = true;
    const tic = async () => {
      if (!vivo) return;
      const e = await mirarEstado();
      if (e?.estado === 'conectado') await cargarConvs();
    };
    tic();
    const t = setInterval(tic, conectado ? 8000 : 4000);
    return () => { vivo = false; clearInterval(t); };
  }, [conectado, mirarEstado, cargarConvs]);

  const abrir = async (c) => {
    setAbierta(c);
    try {
      const { data } = await axios.get(`${API}/wa/conversaciones/${c.wa_conversation_id}/mensajes`);
      setMensajes(data.mensajes || []);
      setConvs((xs) => xs.map((x) => x.wa_conversation_id === c.wa_conversation_id
        ? { ...x, no_leidos: 0 } : x));
      setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    } catch (e) { toast.error('No se pudo abrir la conversación'); }
  };

  const enviar = async () => {
    if (!texto.trim() || !abierta) return;
    setEnviando(true);
    const copia = texto;
    setTexto('');
    try {
      await axios.post(`${API}/wa/enviar`, {
        para: numeroDe(abierta), texto: copia, organization_id: orgId,
        conv_id: abierta.wa_conversation_id,
      });
      const { data } = await axios.get(`${API}/wa/conversaciones/${abierta.wa_conversation_id}/mensajes`);
      setMensajes(data.mensajes || []);
      setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      cargarConvs();
    } catch (e) {
      setTexto(copia);
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    } finally { setEnviando(false); }
  };

  const pedirQrNuevo = async () => {
    try {
      await axios.post(`${API}/wa/reconectar`);
      toast.success('Generando un código nuevo…');
      setQr(null);
      setTimeout(mirarEstado, 3500);
    } catch (e) { toast.error('No se pudo pedir un código nuevo'); }
  };

  const desvincular = async () => {
    if (!window.confirm('¿Cerrar la sesión de WhatsApp? Habrá que volver a escanear el código.')) return;
    try {
      await axios.post(`${API}/wa/desvincular`);
      toast.success('Sesión cerrada');
      setTimeout(mirarEstado, 2500);
    } catch (e) { toast.error('No se pudo cerrar la sesión'); }
  };

  if (cargando) {
    return <p style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 42 }}>Cargando…</p>;
  }

  // ── El puente no responde ─────────────────────────────────────────────────
  if (estado && !estado.disponible) {
    return (
      <div style={{ padding: 44, textAlign: 'center', color: '#94a3b8' }}>
        <AlertTriangle size={38} style={{ color: '#ef4444', marginBottom: 14 }} />
        <h3 style={{ color: '#e2e8f0', margin: '0 0 8px', fontSize: 17 }}>WhatsApp no responde</h3>
        <p style={{ margin: 0, fontSize: 13 }}>{estado.detalle || 'El servicio está parado.'}</p>
      </div>
    );
  }

  // ── Vincular ──────────────────────────────────────────────────────────────
  if (!conectado) {
    return (
      <div style={{ display: 'flex', flexDirection: esMovil ? 'column' : 'row',
                    gap: 34, alignItems: 'center', justifyContent: 'center', padding: '34px 18px' }}>
        <div style={{ background: '#fff', padding: 14, borderRadius: 14, flexShrink: 0 }}>
          {qr
            ? <img src={qr} alt="Código QR de WhatsApp" style={{ width: 250, height: 250, display: 'block' }} />
            : <div style={{ width: 250, height: 250, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', color: '#64748b', fontSize: 13, textAlign: 'center' }}>
                Generando el código…
              </div>}
        </div>

        <div style={{ maxWidth: 340 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 18, color: '#e2e8f0',
                       display: 'flex', alignItems: 'center', gap: 8 }}>
            <Smartphone size={19} style={{ color: VERDE }} /> Vincular WhatsApp
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>
            Se vincula una sola vez. La sesión queda guardada y aguanta reinicios.
          </p>
          <ol style={{ margin: 0, paddingLeft: 19, fontSize: 13, color: '#94a3b8', lineHeight: 2.05 }}>
            <li>Abre WhatsApp en el móvil</li>
            <li>Menú <b style={{ color: '#cbd5e1' }}>⋮</b> → <b style={{ color: '#cbd5e1' }}>Dispositivos vinculados</b></li>
            <li><b style={{ color: '#cbd5e1' }}>Vincular un dispositivo</b></li>
            <li>Apunta a este código</li>
          </ol>
          <p style={{ margin: '16px 0 0', fontSize: 11.5, color: '#475569', lineHeight: 1.6 }}>
            Aparecerá como <b style={{ color: '#64748b' }}>eHome CRM</b> en la lista de dispositivos
            de tu móvil, y desde ahí puedes cerrarla cuando quieras.
          </p>
          <button onClick={pedirQrNuevo}
            style={{ marginTop: 18, padding: '9px 15px', background: 'transparent',
                     border: `1px solid ${ORO}`, borderRadius: 8, color: ORO, cursor: 'pointer',
                     fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 7 }}>
            <RefreshCw size={13} /> El código no funciona, dame otro
          </button>
          {estado?.error && (
            <p style={{ margin: '10px 0 0', fontSize: 11, color: '#64748b' }}>
              El código caduca solo si no se escanea. Pide otro y vuelve a probar.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Conectado: la bandeja ─────────────────────────────────────────────────
  const lista = convs.filter((c) => {
    if (!buscar) return true;
    const t = buscar.toLowerCase();
    return (c.profile_name || '').toLowerCase().includes(t) || (numeroDe(c) || '').includes(t);
  });
  const verLista = !esMovil || !abierta;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: VERDE }}>
          <Check size={14} /> Conectado como <b>{telefono(estado.numero)}</b>
        </span>
        <button onClick={desvincular}
          style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #1e293b',
                   borderRadius: 7, color: '#64748b', cursor: 'pointer', fontSize: 11.5,
                   display: 'flex', alignItems: 'center', gap: 6 }}>
          <LogOut size={12} /> Cerrar sesión
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: esMovil ? 'column' : 'row', gap: 14, minHeight: 440 }}>
        {/* Conversaciones */}
        <div style={{ display: verLista ? 'block' : 'none',
                      width: esMovil ? '100%' : 300, flexShrink: 0,
                      borderRight: !esMovil ? '1px solid #1e293b' : 'none', paddingRight: !esMovil ? 12 : 0 }}>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#475569' }} />
            <input value={buscar} onChange={(e) => setBuscar(e.target.value)}
              placeholder="Buscar…"
              style={{ width: '100%', padding: '8px 10px 8px 31px', background: '#0f172a',
                       border: '1px solid #1e293b', borderRadius: 7, color: '#e2e8f0', fontSize: 12.5 }} />
          </div>

          {lista.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#475569' }}>
              <MessageCircle size={30} style={{ marginBottom: 10, opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6 }}>
                Todavía no hay conversaciones.<br />
                Aparecerán aquí en cuanto entre el primer mensaje.
              </p>
            </div>
          ) : lista.map((c) => (
            <div key={c.wa_conversation_id} onClick={() => abrir(c)}
              style={{ padding: '10px 11px', marginBottom: 3, borderRadius: 9, cursor: 'pointer',
                       background: abierta?.wa_conversation_id === c.wa_conversation_id
                         ? 'rgba(201,168,76,0.1)' : c.no_leidos > 0 ? 'rgba(37,211,102,0.06)' : 'transparent',
                       display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                            background: c.no_leidos > 0 ? VERDE : '#1e293b',
                            color: c.no_leidos > 0 ? '#0a0a0a' : '#94a3b8',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700 }}>
                {(c.profile_name || numeroDe(c) || '?').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: c.no_leidos > 0 ? 700 : 500,
                                 color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap' }}>
                    {c.profile_name || telefono(numeroDe(c))}
                  </span>
                  <span style={{ fontSize: 10.5, color: '#475569', flexShrink: 0 }}>
                    {cuando(c.ultimo_mensaje_at)}
                  </span>
                </div>
                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#64748b',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.ultimo_mensaje || '—'}
                </p>
              </div>
              {c.no_leidos > 0 && (
                <span style={{ background: VERDE, color: '#0a0a0a', borderRadius: 10,
                               padding: '1px 7px', fontSize: 10.5, fontWeight: 700 }}>{c.no_leidos}</span>
              )}
              <button onClick={(e) => { e.stopPropagation(); borrarChat(c); }}
                title="Eliminar este chat del CRM"
                style={{ background: 'none', border: 'none', color: '#334155', cursor: 'pointer',
                         padding: 3, flexShrink: 0, display: 'flex' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#f87171')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#334155')}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        {/* El chat */}
        {abierta && (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 10,
                          borderBottom: '1px solid #1e293b', marginBottom: 10 }}>
              {esMovil && (
                <button onClick={() => setAbierta(null)}
                  style={{ background: 'none', border: 'none', color: ORO, cursor: 'pointer', padding: 0 }}>
                  <ChevronLeft size={19} />
                </button>
              )}
              <div>
                <p style={{ margin: 0, fontSize: 14, color: '#e2e8f0', fontWeight: 600 }}>
                  {abierta.profile_name || telefono(numeroDe(abierta))}
                </p>
                <p style={{ margin: 0, fontSize: 11,
                            color: abierta.telefono === null ? '#b45309' : '#475569' }}>
                  {abierta.telefono === null
                    ? 'WhatsApp aún no ha dado el móvil de este contacto'
                    : telefono(numeroDe(abierta))}
                </p>
              </div>
              {/* Dar de alta a esta persona en el CRM. El telefono y el nombre
                  salen de WhatsApp; lo demas se completa luego en su ficha. */}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['vendedor', 'Vendedor'], ['comprador', 'Comprador'], ['profesional', 'Profesional']].map(([cat, etiqueta]) => (
                  <button key={cat} onClick={() => asignar(cat)} disabled={!!asignando}
                    title={`Dar de alta a este contacto como ${etiqueta.toLowerCase()}`}
                    style={{ padding: '5px 9px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                             border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.1)',
                             color: ORO, cursor: asignando ? 'default' : 'pointer',
                             opacity: asignando ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {asignando === cat ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
                    {etiqueta}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', maxHeight: esMovil ? 380 : 380,
                          padding: '4px 2px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {mensajes.map((m) => {
                const mio = m.direction === 'saliente';
                return (
                  <div key={m.wa_message_id} style={{ alignSelf: mio ? 'flex-end' : 'flex-start', maxWidth: '76%' }}>
                    <div style={{ padding: '8px 11px', borderRadius: 11,
                                  background: mio ? 'rgba(37,211,102,0.14)' : '#1e293b',
                                  border: `1px solid ${mio ? 'rgba(37,211,102,0.25)' : '#1e293b'}` }}>
                      {m.tipo === 'imagen' && m.media_url && (
                        <a href={m.media_url} target="_blank" rel="noreferrer">
                          <img src={m.media_url} alt="foto"
                               style={{ maxWidth: '100%', borderRadius: 8, display: 'block',
                                        marginBottom: m.texto ? 6 : 0 }} />
                        </a>
                      )}
                      {m.tipo === 'audio' && m.media_url && (
                        <audio controls src={m.media_url}
                               style={{ width: '100%', maxWidth: 240, marginBottom: 4 }} />
                      )}
                      {(m.tipo === 'documento' || m.tipo === 'video') && m.media_url && (
                        <a href={m.media_url} target="_blank" rel="noreferrer"
                           style={{ display: 'flex', alignItems: 'center', gap: 6, color: VERDE,
                                    fontSize: 12.5, textDecoration: 'none', marginBottom: m.texto ? 6 : 0 }}>
                          <FileText size={14} /> {m.tipo === 'video' ? 'Ver el vídeo' : 'Abrir el archivo'}
                        </a>
                      )}
                      {(m.texto || (!m.media_url && m.tipo !== 'texto')) && (
                        <p style={{ margin: 0, fontSize: 13, color: '#e2e8f0', lineHeight: 1.55,
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {m.texto || `[${m.tipo}]`}
                        </p>
                      )}
                      {m.transcripcion && (
                        // Lo que dice la nota de voz, escrito. Se marca como
                        // transcripcion para que nadie lo confunda con algo que
                        // el cliente haya tecleado.
                        <p style={{ margin: '5px 0 0', fontSize: 12, color: '#94a3b8',
                                    fontStyle: 'italic', lineHeight: 1.5,
                                    borderLeft: '2px solid #334155', paddingLeft: 7 }}>
                          {m.transcripcion}
                        </p>
                      )}
                    </div>
                    <p style={{ margin: '2px 4px 0', fontSize: 10, color: '#475569',
                                textAlign: mio ? 'right' : 'left' }}>
                      {mio && m.emisor_nombre ? `${m.emisor_nombre} · ` : ''}{cuando(m.timestamp)}
                    </p>
                  </div>
                );
              })}
              <div ref={finRef} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 10,
                          borderTop: '1px solid #1e293b', alignItems: 'center' }}>
              <input ref={ficheroRef} type="file" style={{ display: 'none' }}
                     onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) mandarArchivo(f); }} />
              <button onClick={() => ficheroRef.current?.click()} disabled={subiendo || grabando}
                title="Adjuntar una foto o un archivo"
                style={{ background: 'none', border: 'none', color: subiendo ? '#334155' : '#64748b',
                         cursor: subiendo || grabando ? 'default' : 'pointer', padding: 4, display: 'flex' }}>
                {subiendo ? <Loader2 size={17} className="animate-spin" /> : <Paperclip size={17} />}
              </button>
              <button onClick={grabando ? pararGrabacion : empezarGrabacion} disabled={subiendo}
                title={grabando ? 'Parar y enviar la nota de voz' : 'Grabar una nota de voz'}
                style={{ background: grabando ? 'rgba(239,68,68,0.15)' : 'none',
                         border: 'none', borderRadius: 8,
                         color: grabando ? '#f87171' : '#64748b',
                         cursor: subiendo ? 'default' : 'pointer', padding: 4, display: 'flex' }}>
                {grabando ? <Square size={16} /> : <Mic size={17} />}
              </button>
              {grabando && (
                <span style={{ fontSize: 11.5, color: '#f87171', fontWeight: 600 }}>
                  grabando {segundos}s
                </span>
              )}
              <input value={texto} onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), enviar())}
                placeholder="Escribe un mensaje…"
                style={{ flex: 1, padding: '10px 13px', background: '#0f172a',
                         border: '1px solid #1e293b', borderRadius: 20, color: '#e2e8f0', fontSize: 13 }} />
              <button onClick={enviar} disabled={enviando || !texto.trim()}
                style={{ padding: '10px 15px', background: VERDE, color: '#0a0a0a', border: 'none',
                         borderRadius: 20, cursor: enviando || !texto.trim() ? 'default' : 'pointer',
                         opacity: enviando || !texto.trim() ? 0.45 : 1, display: 'flex',
                         alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700 }}>
                <Send size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
